/**
 * Form helpers for create/edit holiday views.
 */
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';
import type { CreateHolidayDto, Holiday, UpdateHolidayDto } from '@/types/holiday.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Holiday type stored on the form (drives establishment / zone visibility). */
type HolidayType = CreateHolidayDto['type'];

/** Empty create form; type defaults to national. */
export function emptyCreateHolidayForm(): CreateHolidayDto {
  return {
    name: '',
    type: 'national',
    startDate: '',
    endDate: '',
    description: '',
    isRecurringYearly: false,
  };
}

/** Patch one field. */
export function applyHolidayFormField<T extends CreateHolidayDto | UpdateHolidayDto>(
  form: T,
  field: keyof T,
  value: string | boolean,
): T {
  return { ...form, [field]: value };
}

/** Changing type clears the field that no longer applies (establishment vs zone). */
export function applyHolidayTypeChange<T extends CreateHolidayDto | UpdateHolidayDto>(
  form: T,
  value: string,
): T {
  const type = value as HolidayType;
  return {
    ...form,
    type,
    establishmentId: type === 'establishment_specific' ? form.establishmentId : undefined,
    vacationZoneId: type === 'regional' ? form.vacationZoneId : undefined,
  };
}

/** {@link DatePicker} value as `YYYY-MM-DD` (empty when unset). */
function toIsoDateInput(value: Date | string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toISOString().split('T')[0];
}

/** Hydrate the edit form from GET holiday. */
export function holidayToFormData(holiday: Holiday): UpdateHolidayDto {
  return {
    name: holiday.name,
    type: holiday.type,
    startDate: toIsoDateInput(holiday.startDate),
    endDate: toIsoDateInput(holiday.endDate),
    description: holiday.description || '',
    establishmentId: holiday.establishmentId || undefined,
    vacationZoneId: holiday.vacationZoneId || undefined,
    isRecurringYearly: holiday.isRecurringYearly,
  };
}

/** Zone {@link Select} options plus an empty "all zones" row. */
export function mapVacationZoneOptions(
  zones: Array<{ id: string; name: string; code: string }>,
): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'Toutes les zones' },
    ...zones.map((zone) => ({
      value: zone.id,
      label: `${zone.name} (${zone.code})`,
    })),
  ];
}

/** Establishment {@link Select} options (label includes UAI when present). */
export function mapHolidayEstablishmentOptions(
  establishments: Array<{ id: string; name: string; uaiCode?: string | null }> | undefined,
): Array<{ value: string; label: string }> {
  return (
    establishments?.map((establishment) => ({
      value: establishment.id,
      label: formatEstablishmentSelectLabel(establishment),
    })) || []
  );
}

/** Resolve the display name for an establishment-specific holiday. */
function resolveHolidayEstablishmentName(
  form: CreateHolidayDto | UpdateHolidayDto,
  establishments: Array<{ id: string; name?: string | null }> | undefined,
): string | undefined {
  if (form.type !== 'establishment_specific' || !form.establishmentId) return undefined;
  return establishments?.find((establishment) => establishment.id === form.establishmentId)?.name;
}

/** Create DTO; zone id is sent only for regional holidays. */
export function buildCreateHolidayDto(
  form: CreateHolidayDto,
  establishments: Array<{ id: string; name?: string | null }> | undefined,
): CreateHolidayDto {
  return {
    ...form,
    establishmentName: resolveHolidayEstablishmentName(form, establishments),
    vacationZoneId:
      form.type === 'regional' && form.vacationZoneId ? form.vacationZoneId : undefined,
  };
}

/** Update DTO; non-regional holidays clear `vacationZoneId`. */
export function buildUpdateHolidayDto(
  form: UpdateHolidayDto,
  establishments: Array<{ id: string; name?: string | null }> | undefined,
): UpdateHolidayDto {
  return {
    ...form,
    establishmentName: resolveHolidayEstablishmentName(form, establishments),
    vacationZoneId: form.type === 'regional' ? form.vacationZoneId || null : null,
  };
}

/** Central error copy from {@link getErrorMessage}. */
export function getHolidayApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
